import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { toast } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/Navbar";
import LandingPage from "@/pages/LandingPage";
import AuthPage from "@/pages/AuthPage";
import OnboardingWizard from "@/pages/OnboardingWizard";
import PolicyholderDashboard from "@/pages/PolicyholderDashboard";
import ClaimDetailPage from "@/pages/ClaimDetailPage";
import ContractorDashboard from "@/pages/ContractorDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import CarrierGuidePage from "@/pages/CarrierGuidePage";
import VerifyCertificationPage from "@/pages/VerifyCertificationPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import { isNative } from "@/native/platform";
import { registerDeepLinkListener } from "@/native/deepLinks";
import { attachPushHandlers, registerPush } from "@/native/push";
import "@/App.css";

// Role constants hoisted out of JSX so the same array reference is reused
// across renders — passing inline arrays as props would cause every
// ProtectedRoute to receive a new identity each time AppRoutes re-renders.
const ROLES_PH = ["policyholder"];
const ROLES_CONTRACTOR = ["contractor"];
const ROLES_ADMIN = ["admin"];
const ROLES_ALL = ["policyholder", "contractor", "admin"];

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Native-only: wire deep links + push notifications once the user is known.
  useEffect(() => {
    if (!isNative()) return;
    const unsubscribe = registerDeepLinkListener((path) => navigate(path));
    if (user) {
      // Toast foregrounded pushes with the same style as status toasts so the
      // UX is consistent between a banner and in-app notification.
      attachPushHandlers((title, body) =>
        toast.message(title, { description: body }),
      );
      registerPush();
    }
    return unsubscribe;
  }, [user, navigate]);

  return (
    <div className="min-h-screen bg-[#F9F8F6] text-[#111827]">
      <Navbar />
      <div className="pt-20">
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<AuthPage mode="login" />} />
          <Route path="/signup" element={<AuthPage mode="signup" />} />
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute roles={ROLES_PH}>
                <OnboardingWizard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute roles={ROLES_PH}>
                <PolicyholderDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/claim/:id"
            element={
              <ProtectedRoute roles={ROLES_ALL}>
                <ClaimDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/contractor"
            element={
              <ProtectedRoute roles={ROLES_CONTRACTOR}>
                <ContractorDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute roles={ROLES_ADMIN}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route path="/guide/:carrierId" element={<CarrierGuidePage />} />
          <Route path="/verify/:certHash" element={<VerifyCertificationPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
      <Toaster richColors position="top-right" />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
